// Verify2FAHandler.jsx
//
// The "Finish signing in" link in the 2FA email lands here with the uid and the code in
// the URL.
//
// SS-4131 changed what that link can do. /verify-2fa no longer accepts a code on its
// own: it also wants the pending-login credential the password step handed back, which
// proves the sender is the one who passed the password check. A correct code on its own
// is no longer proof of anything.
//
// Following a link from an email is always a fresh page load, so nothing this page can
// reach in memory survives from the tab that signed in — which is why SS-4185 has Auth
// persist that credential for the life of the challenge. So there are two outcomes here,
// and both are intended:
//
//   * opened in the browser that started the sign-in, Auth.pendingLogin(uid) has the
//     credential, it goes with the code, and the click finishes the sign-in — the same
//     two proofs as typing the six digits into the tab that is waiting;
//   * opened anywhere else — another device, another profile, a private window, a
//     forwarded email — that browser never did the password step and has nothing to
//     present, so the api refuses and this page says where the sign-in can be finished.
//
// The request is made either way. An api that has not shipped SS-4131 yet verifies the
// code on its own, and refusing to ask would strand the link against it; an api that has
// refuses a credential-less call before it counts an attempt, so asking costs nothing.
import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Button, Flex, Text } from "@chakra-ui/react";
import { Auth, cqAPI } from "../../lib/Auth";

export default function Verify2FAHandler() {
  const [searchParams] = useSearchParams();
  const uid = searchParams.get("uid");
  const code = searchParams.get("code");
  const trustDevice = searchParams.get("trustDevice") ?? false;
  const redirectTo = searchParams.get("redirectTo");
  const navigate = useNavigate();
  const [error, setError] = useState(null);
  const [needsSignIn, setNeedsSignIn] = useState(false);

  useEffect(() => {

    const handleVerify = async (pendingLoginToken) => {
      try {
        const response = await cqAPI.post(
          `/verify-2fa?uid=${uid}&code=${code}&trustDevice=${trustDevice}`,
          pendingLoginToken ? { pendingLoginToken } : undefined
        );
        if (response.type === "ok") {
          // 2FA success. The challenge is spent; the credential cannot be reused.
          Auth.clearPendingLogin();
          Auth.storeUser(response.data.user);
          if (response.data.rememberMeToken) {
            localStorage.setItem('rememberMeToken', response.data.rememberMeToken);
          }
          const storedRedirect = localStorage.getItem("post2faRedirect");
          const nextPath = (typeof redirectTo === "string" && redirectTo.startsWith("/"))
            ? redirectTo
            : ((typeof storedRedirect === "string" && storedRedirect.startsWith("/")) ? storedRedirect : "/");
          localStorage.removeItem("post2faRedirect");
          navigate(nextPath);
        } else if (!pendingLoginToken) {
          // refused, and this browser had nothing to prove the password step with:
          // finishing here is not possible, so say where it can be finished
          setNeedsSignIn(true);
        } else {
          setError(response.msg || "Code is incorrect or expired.");
          console.error(response.msg || "Code is incorrect or expired.");
        }
      } catch (err) {
        setError(err.message || "Something went wrong.")
        console.error(err.message || "Something went wrong.");
      }
    };

    // A link with nothing to verify cannot go anywhere; that used to sit on
    // "Verifying your device, please wait..." for ever.
    if (!uid || !code) {
      setNeedsSignIn(true);
      return;
    }
    handleVerify(Auth.pendingLogin(uid));
  }, []);

  if (needsSignIn) {
    return (
      <Flex minH="100vh" direction="column" justify="center" align="center" gap={4} p={8}>
        <Text fontWeight="bold">Enter the code to finish signing in</Text>
        <Text textAlign="center" maxW="480px">
          For your security this link only works in the browser where you started
          signing in. Type the six-digit code from the same email into that window, or
          sign in again here to get a new code.
        </Text>
        <Button onClick={() => navigate("/")}>Sign in</Button>
      </Flex>
    );
  }

  return (
    <div>
      Verifying your device, please wait...
      {error && (
        <div style={{ color: "red", marginTop: "1rem" }}>
          Error: {error}
        </div>
      )}
    </div>
  );
}
